import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Lock, X, Globe as GlobeIcon } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";

export function FlagBadge({ code, className = "" }) {
  if (!code) return null;
  if (typeof code === "string" && code.length === 2 && /^[a-zA-Z]{2}$/.test(code)) {
    const flag = String.fromCodePoint(...[...code.toUpperCase()].map(c => 127397 + c.charCodeAt(0)));
    return <span className={cx("text-base leading-none", className)}>{flag}</span>;
  }
  return <GlobeIcon size={14} className={cx("text-teal", className)} />;
}

/** Animated dot for "Bookable" / live chips. */
export function LiveDot({ size = 8, color = "#5EEAD4" }) {
  return (
    <span className="relative inline-block" style={{ width: size, height: size }}>
      <span className="absolute inset-0 rounded-full live-dot-ring" style={{ background: color }} />
      <span className="relative block rounded-full live-dot" style={{ width: size, height: size, background: color, boxShadow: `0 0 6px ${color}` }} />
    </span>
  );
}

export function StatusChip({ status }) {
  if (status === "active") {
    return (
      <span className="chip success" data-testid="status-bookable">
        <LiveDot size={7} />
        Bookable
      </span>
    );
  }
  return (
    <span className="chip" data-testid="status-coming-soon">
      <Lock size={11}/> Coming soon
    </span>
  );
}

export default function DestinationCard({ d, onWaitlist }) {
  const isActive = d.status === "active";
  return (
    <article className={cx("luxe-card group h-[360px] md:h-[480px] relative overflow-hidden")}
      data-testid={`destination-card-${d.slug}`}>
      <img
        src={d.hero_media_url}
        alt={d.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110 opacity-90"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      {!isActive && <div className="absolute inset-0 backdrop-blur-[2px]"/>}

      <div className="relative h-full flex flex-col justify-between p-5 md:p-7">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 chip">
            <FlagBadge code={d.flag_emoji} />
            <span className="!tracking-widest2">{d.country}</span>
          </div>
          <StatusChip status={d.status} />
        </div>

        <div>
          <h3 className="section-title text-3xl md:text-4xl text-bone">
            {d.name}
          </h3>
          {d.hero_subtitle && (
            <p className="mt-2 text-sm text-muted max-w-[28ch]">{d.hero_subtitle}</p>
          )}
          <div className="mt-5">
            {isActive ? (
              <Link
                to={d.slug === "custom-trip" ? "/custom-trip" : `/destinations/${d.slug}`}
                className="btn btn-primary text-[12px]"
                data-testid={`btn-explore-${d.slug}`}
              >
                {d.slug === "custom-trip" ? "Plan a custom trip" : `Explore ${d.name}`}
                <ArrowUpRight size={14}/>
              </Link>
            ) : (
              <button
                onClick={() => onWaitlist?.(d)}
                className="btn btn-outline text-[12px]"
                data-testid={`btn-waitlist-${d.slug}`}
              >
                Join waitlist
                <ArrowUpRight size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export function WaitlistModal({ destination, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!destination) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/waitlist", {
        email,
        name: name || undefined,
        destination_slug: destination.slug,
        source: "destination_card",
      });
      setDone(true);
    } catch (err) {
      setError(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="waitlist-modal">
      <div className="luxe-card relative w-full max-w-md p-7" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full border hairline text-muted hover:text-bone hover:border-teal/60 transition z-10"
          data-testid="waitlist-close-btn">
          <X size={16}/>
        </button>

        <div className="flex items-center justify-between mb-4 pr-9">
          <div>
            <div className="eyebrow">Waitlist</div>
            <h3 className="section-title text-3xl mt-1">{destination.name}</h3>
          </div>
          <FlagBadge code={destination.flag_emoji}/>
        </div>
        {!done ? (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-muted">We're curating a verified provider network. Join the list and we'll let you know the moment {destination.name} opens — with a priority booking window.</p>
            <input className="input-bare" placeholder="Full name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="waitlist-name"/>
            <input required type="email" className="input-bare" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="waitlist-email"/>
            {error && <div className="text-rose-300 text-xs">{error}</div>}
            <button type="submit" disabled={busy} className="btn btn-primary w-full" data-testid="waitlist-submit">
              {busy ? "Joining…" : "Notify me"}
            </button>
          </form>
        ) : (
          <div className="text-center py-6">
            <div className="font-script text-3xl text-teal mb-2">À bientôt.</div>
            <p className="text-sm text-muted">You're on the {destination.name} list. We'll be in touch.</p>
            <button onClick={onClose} className="btn btn-outline mt-6" data-testid="waitlist-close">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
